import { InfoOutlined } from '@mui/icons-material'
import { Box, Tooltip, TooltipProps } from '@mui/material'
import { withStyles } from '@mui/styles'
import { t } from 'i18next'
import { ReactElement, ReactNode, useState, useEffect } from 'react'
import { isCypress } from 'utils/cypress'
import { isTestEnv } from 'utils/env'

interface GroupcalTooltipProps {
  children: ReactElement | ReactNode
  title: ReactNode
  delay?: number
  color?: string
  enterDelay?: number
  placement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top'
  backgroundColor?: string
  textColor?: string
  textSize?: string
  enterNextDelay?: number
  hidden?: boolean
  tooltipStyles?: React.CSSProperties
  showArrow?: boolean
  byPassCypressTest?: boolean
  image?: string
  link?: string
  linkText?: string
}

export default function GroupcalTooltip(props: GroupcalTooltipProps) {
  const [imageLoaded, setImageLoaded] = useState(!props.image)

  // Pre-load image on mount
  useEffect(() => {
    if (props.image) {
      const img = new Image()
      img.onload = () => setImageLoaded(true)
      img.onerror = () => setImageLoaded(true) // Proceed even if image fails to load
      img.src = props.image
    }
  }, [props.image])

  if (props.hidden || (isCypress() && !props.byPassCypressTest)) {
    return <div>{props.children}</div>
  }

  // Build tooltip content with optional image and link
  const tooltipContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%'
      }}
    >
      {/* Main content/title */}
      <Box sx={{ mb: props.image ? '4px' : '0px' }}>{props.title}</Box>

      {/* Image - pre-loaded so size is known on hover */}
      {props.image && imageLoaded && (
        <img
          src={props.image}
          alt="tooltip-image"
          style={{
            maxWidth: '300px',
            height: 'auto',
            borderRadius: '8px',
            margin: '0 auto',
            marginBottom: props.link ? '8px' : '0px',
            display: 'block'
          }}
        />
      )}

      {/* Read more link - "Read more - " prefix with bold link text */}
      {props.link && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontWeight: 'bold' }}>{`${t('readMore')} - `}</span>
          <Box
            component="a"
            href={props.link}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: '#1976d2',
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': {
                color: '#1565c0'
              }
            }}
          >
            {props.linkText}
          </Box>
        </Box>
      )}
    </Box>
  )

  return (
    <Tooltip
      leaveDelay={200}
      enterDelay={props.enterDelay ?? 500}
      enterNextDelay={props.enterNextDelay ?? 600}
      placement={props.placement ?? 'bottom'}
      arrow={props.showArrow}
      title={tooltipContent}
      slotProps={{
        tooltip: {
          sx: {
            display: 'block',
            padding: '12px',
            fontSize: props.textSize ?? '12px',
            lineHeight: '22px',
            letterSpacing: '0.5px',
            margin: '4px !important',
            borderRadius: '8px',
            maxWidth: '400px',
            backgroundColor:
              props.backgroundColor || 'default-background-color',
            color: props.textColor || 'default-text-color',
            boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.25)',
            '& .MuiTooltip-arrow::before': {
              backgroundColor: 'white !important'
            },
            ...props.tooltipStyles
          }
        },
        popper: {
          sx: {
            zIndex: 999998
          }
        }
      }}
    >
      {/* This div now controls the visibility of the tooltip */}
      <Box sx={{ display: 'flex', height: '100%' }}>{props.children}</Box>
    </Tooltip>
  )
}
